import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import type { TestingChatActivity } from "@/stores/testingStore";

/**
 * Status glyph for one activity row (planner tool call, run-time step, org inspection).
 *
 * Single home for this component — it previously existed as two copies (ExecutionFeed's private one
 * and ChatSseActivity's exported one) that had to be kept in sync by hand.
 *
 * `warning` deliberately renders amber, not green: the call returned but produced no usable evidence
 * (tool unavailable, agent abstained, empty FLS graph). Green would claim evidence never gathered.
 */
export function ActivityDot({
	status,
	streaming,
}: {
	status?: TestingChatActivity["status"];
	streaming: boolean;
}) {
	if (status === "blocked") return <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-primary" />;
	if (status === "error") return <XCircle className="mt-0.5 h-3 w-3 shrink-0 text-destructive-strong" />;
	if (status === "warning") return <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning-strong" />;
	if (status === "ok") return <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-success-strong" />;
	if (streaming) return <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin text-primary" />;
	return <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-muted-foreground/50" />;
}

/**
 * The live/idle dot used by session rows and run rows. `busy` pulses; every other state is static so
 * only genuinely-active work draws the eye.
 */
export function RunStatusDot({
	status,
	className,
}: {
	status: "ok" | "err" | "warn" | "busy" | "neutral";
	className?: string;
}) {
	if (status === "busy") {
		return (
			<span className={`relative flex h-2 w-2 shrink-0 ${className ?? ""}`}>
				<span
					data-tst-decorative-motion
					className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60"
				/>
				<span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
			</span>
		);
	}
	const tone =
		status === "ok"
			? "bg-success"
			: status === "err"
				? "bg-destructive"
				: status === "warn"
					? "bg-warning"
					: "bg-muted-foreground/40";
	return <span className={`h-2 w-2 shrink-0 rounded-full ${tone} ${className ?? ""}`} />;
}
